'use client';

/**
 * GlassSelect Component
 *
 * A glassmorphism select dropdown with option-level permission/tier/license locking.
 * Locked options are visible but disabled with a lock icon and tooltip.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import LockIcon from '@mui/icons-material/Lock';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  type SelectProps,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import type { LockConfig, LockState } from '@ops-matrix/shared-types';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getTierLabel, usePermissionOptional } from '../../providers/PermissionProvider';
import { glassEffects, glowEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { transientOptions } from '../../utils/styledUtils';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

/**
 * Option with permission/tier/license locking support
 */
export interface GlassSelectOption {
  /** Option value */
  value: string | number;

  /** Display label */
  label: string;

  /** Whether option is disabled (independent of lock state) */
  disabled?: boolean;

  /** Single permission required to select this option */
  permission?: string;

  /** Any of these permissions grants access (OR logic) */
  anyPermission?: string[];

  /** All of these permissions required (AND logic) */
  allPermissions?: string[];

  /** Minimum subscription tier required */
  requiredTier?: LockConfig['requiredTier'];

  /** Specific license type required */
  requiredLicense?: string;

  /** Any of these licenses grants access (OR logic) */
  anyLicense?: string[];

  /** Optional icon to display */
  icon?: React.ReactNode;

  /** Secondary description text */
  description?: string;
}

export interface GlassSelectProps extends Omit<SelectProps, 'variant' | 'children'> {
  /**
   * Select options with optional locking
   */
  options: GlassSelectOption[];

  /**
   * Helper text to display below the select
   */
  helperText?: string;

  /**
   * Glow color variant
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /**
   * Enable glassmorphism effect on dropdown
   * @default true
   */
  glassDropdown?: boolean;

  /**
   * Whether to show lock icons on locked options
   * @default true
   */
  showLockIcon?: boolean;

  /**
   * Whether to show tier badge when tier-locked
   * @default true
   */
  showTierBadge?: boolean;

  /**
   * Custom handler when a locked option is clicked
   * If not provided, default behavior from PermissionProvider is used
   */
  onLockedOptionClick?: (option: GlassSelectOption) => void;

  /**
   * Select variant (always outlined for design consistency)
   * @default 'outlined'
   */
  variant?: 'outlined';
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledFormControlProps {
  $isDark: boolean;
  $glowColor: 'primary' | 'secondary' | 'mixed';
  $error?: boolean;
  $size: 'small' | 'medium';
}

const getGlowValue = (color: 'primary' | 'secondary' | 'mixed', intensity: 'subtle' | 'medium'): string =>
  glowEffects[color][intensity];

// Apple HIG-aligned sizing — matches MagicInput so Glass form controls are visually
// consistent. Small = 36px dense (filters, toolbars); Medium = 44px touch target (forms).
const SIZE_TOKENS = {
  small: { padding: '8.5px 14px', fontSize: '0.875rem', labelFontSize: '0.875rem' },
  medium: { padding: '11.5px 16px', fontSize: '0.9375rem', labelFontSize: '0.9375rem' },
} as const;

const StyledFormControl = styled(
  FormControl,
  transientOptions,
)<StyledFormControlProps>(({ theme, $isDark, $glowColor, $error, $size }) => {
  const isMaterial = theme.designGuideline === 'material';
  const focusColor = $error
    ? theme.palette.error.main
    : theme.palette[$glowColor === 'mixed' ? 'primary' : $glowColor].main;
  const tokens = SIZE_TOKENS[$size];

  // ── Material Design 3 ──
  if (isMaterial) {
    return {
      '& .MuiOutlinedInput-root': {
        transition: transitionPresets.input,
        borderRadius: 8, // MD3 text field radius
        background: 'transparent',

        '& fieldset': {
          borderColor: $error ? theme.palette.error.main : $isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
          borderWidth: 1,
          transition: transitionPresets.input,
        },

        '& .MuiSelect-select.MuiSelect-outlined': {
          fontSize: tokens.fontSize,
          padding: tokens.padding,
          minHeight: 'unset',
          display: 'flex',
          alignItems: 'center',
        },

        '&:hover fieldset': {
          borderColor: $error ? theme.palette.error.main : $isDark ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.20)',
        },

        '&.Mui-focused': {
          '& fieldset': {
            borderColor: focusColor,
            borderWidth: 2,
          },
        },
      },

      '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,
        fontSize: tokens.labelFontSize,

        '&.MuiInputLabel-shrink': {
          backgroundColor: $isDark ? theme.palette.background.paper : theme.palette.background.default,
          padding: '0 6px',
          marginLeft: '-4px',
          borderRadius: 2,
        },

        '&.Mui-focused': {
          color: focusColor,
        },
      },
    };
  }

  // ── Apple Liquid Glass (original) ──
  return {
    '& .MuiOutlinedInput-root': {
      transition: transitionPresets.input,
      borderRadius: 8,
      // ── Frosted glass input ──
      background: $isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.50)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',

      '& fieldset': {
        borderColor: $error ? theme.palette.error.main : $isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.65)',
        borderWidth: '0.5px',
        transition: transitionPresets.input,
      },

      '& .MuiSelect-select.MuiSelect-outlined': {
        fontSize: tokens.fontSize,
        padding: tokens.padding,
        minHeight: 'unset',
        display: 'flex',
        alignItems: 'center',
      },

      '&:hover fieldset': {
        borderColor: $error ? theme.palette.error.main : $isDark ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.85)',
      },

      '&.Mui-focused': {
        '& fieldset': {
          borderColor: focusColor,
          borderWidth: 2,
        },
        boxShadow: $error ? `0 0 0 3px ${alpha(theme.palette.error.main, 0.2)}` : getGlowValue($glowColor, 'medium'),
      },
    },

    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: tokens.labelFontSize,

      // ── Glass-aware floating label background ──
      '&.MuiInputLabel-shrink': {
        backgroundColor: $isDark ? 'rgba(28,28,30,0.85)' : 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        padding: '0 6px',
        marginLeft: '-4px',
        borderRadius: 4,
      },

      '&.Mui-focused': {
        color: focusColor,
      },
    },
  };
});

interface StyledMenuItemProps {
  $isDark: boolean;
  $isLocked: boolean;
}

const StyledMenuItem = styled(
  MenuItem,
  transientOptions,
)<StyledMenuItemProps>(({ theme, $isDark, $isLocked }) => {
  const isMaterial = theme.designGuideline === 'material';

  return {
    borderRadius: isMaterial ? 4 : 8, // MD3 menu item radius
    margin: theme.spacing(0.5, 1),
    minHeight: isMaterial ? 40 : 44,
    fontSize: isMaterial ? '0.875rem' : '0.9375rem',
    transition: transitionPresets.button,
    opacity: $isLocked ? 0.7 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),

    '&:hover': {
      backgroundColor: $isLocked
        ? 'transparent'
        : $isDark
          ? alpha(theme.palette.primary.main, isMaterial ? 0.08 : 0.15)
          : alpha(theme.palette.primary.main, isMaterial ? 0.06 : 0.1),
      cursor: $isLocked ? 'default' : 'pointer',
    },

    '&.Mui-selected': {
      backgroundColor: $isDark
        ? alpha(theme.palette.primary.main, isMaterial ? 0.16 : 0.25)
        : alpha(theme.palette.primary.main, isMaterial ? 0.1 : 0.15),
      '&:hover': {
        backgroundColor: $isDark
          ? alpha(theme.palette.primary.main, isMaterial ? 0.2 : 0.3)
          : alpha(theme.palette.primary.main, isMaterial ? 0.14 : 0.2),
      },
    },

    '&.Mui-disabled': {
      opacity: $isLocked ? 0.7 : isMaterial ? 0.38 : 0.5,
    },
  };
});

// ============================================================================
// Component
// ============================================================================

/**
 * GlassSelect
 *
 * A glassmorphism select dropdown with option-level permission/tier/license locking.
 *
 * @example
 * ```tsx
 * // Options with different lock types
 * const options = [
 *   { value: 'basic', label: 'Basic Report' },
 *   { value: 'advanced', label: 'Advanced Report', requiredTier: SubscriptionTier.PROFESSIONAL },
 *   { value: 'compliance', label: 'Compliance Report', permission: 'reports:compliance' },
 *   { value: 'audit', label: 'Audit Report', requiredLicense: 'NMLS_INDIVIDUAL' },
 * ];
 *
 * <GlassSelect
 *   label="Report Type"
 *   options={options}
 *   value={selectedReport}
 *   onChange={(e) => setSelectedReport(e.target.value)}
 * />
 * ```
 */
export const GlassSelect = forwardRef<HTMLDivElement, GlassSelectProps>(
  (
    {
      options,
      label,
      helperText,
      error,
      glowColor = 'primary',
      glassDropdown = true,
      showLockIcon = true,
      showTierBadge = true,
      onLockedOptionClick,
      fullWidth = true,
      size = 'medium',
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const glass = isDark ? glassEffects.dark : glassEffects.light;
    const permissionContext = usePermissionOptional();
    const { t } = useI18n();

    // Compute lock state for each option
    const optionsWithLockState = useMemo(() => {
      return options.map((option) => {
        const lockConfig: LockConfig | null =
          option.permission ||
          option.anyPermission ||
          option.allPermissions ||
          option.requiredTier ||
          option.requiredLicense ||
          option.anyLicense
            ? {
                permission: option.permission,
                anyPermission: option.anyPermission,
                allPermissions: option.allPermissions,
                requiredTier: option.requiredTier,
                requiredLicense: option.requiredLicense,
                anyLicense: option.anyLicense,
              }
            : null;

        const lockState = lockConfig && permissionContext ? permissionContext.getLockState(lockConfig) : null;

        return {
          ...option,
          lockState,
          isLocked: !!lockState,
        };
      });
    }, [options, permissionContext]);

    // Handle click on locked option
    const handleLockedClick = useCallback(
      (option: GlassSelectOption, lockState: LockState) => {
        if (onLockedOptionClick) {
          onLockedOptionClick(option);
        } else if (lockState.onLockedClick) {
          lockState.onLockedClick();
        }
      },
      [onLockedOptionClick],
    );

    // Render individual option
    const renderOption = (optionWithLock: (typeof optionsWithLockState)[0]) => {
      const { lockState, isLocked, icon, description, ...option } = optionWithLock;
      const isDisabled = option.disabled || isLocked;

      const menuItem = (
        <StyledMenuItem
          key={option.value}
          value={option.value}
          disabled={isDisabled}
          $isDark={isDark}
          $isLocked={isLocked}
          onClick={
            isLocked && lockState
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLockedClick(option, lockState);
                }
              : undefined
          }
        >
          {/* Option icon */}
          {icon && (
            <ListItemIcon sx={{ minWidth: 32, color: isDisabled ? 'action.disabled' : 'inherit' }}>{icon}</ListItemIcon>
          )}

          {/* Label and description */}
          <ListItemText
            primary={option.label}
            secondary={description}
            sx={{
              flex: 1,
              '& .MuiListItemText-primary': {
                color: isDisabled ? 'text.disabled' : 'text.primary',
              },
              '& .MuiListItemText-secondary': {
                color: isDisabled ? 'text.disabled' : 'text.secondary',
                fontSize: '0.75rem',
              },
            }}
          />

          {/* Lock indicator */}
          {isLocked && showLockIcon && lockState && (
            <>
              <LockIcon
                sx={{
                  fontSize: iconSizes.sm,
                  color:
                    lockState.type === 'tier'
                      ? theme.palette.warning.main
                      : lockState.type === 'license'
                        ? theme.palette.info.main
                        : theme.palette.action.disabled,
                  ml: 1,
                }}
              />
              {showTierBadge && lockState.type === 'tier' && lockState.requiredTier && (
                <GlassChip
                  label={getTierLabel(lockState.requiredTier)}
                  size="small"
                  color="warning"
                  sx={{
                    ml: 0.5,
                    height: 18,
                    fontSize: '0.75rem',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              )}
            </>
          )}
        </StyledMenuItem>
      );

      // Wrap locked options with tooltip
      if (isLocked && lockState) {
        return (
          <GlassTooltip key={option.value} title={lockState.reason ?? t('secure.noAccess')} arrow placement="left">
            <span style={{ display: 'block' }}>{menuItem}</span>
          </GlassTooltip>
        );
      }

      return menuItem;
    };

    return (
      <StyledFormControl
        ref={ref}
        fullWidth={fullWidth}
        error={error}
        size={size === 'small' ? 'small' : undefined}
        $isDark={isDark}
        $glowColor={glowColor}
        $size={size === 'small' ? 'small' : 'medium'}
      >
        {label && <InputLabel size={size === 'small' ? 'small' : undefined}>{label}</InputLabel>}
        <Select
          label={label}
          size={size === 'small' ? 'small' : undefined}
          MenuProps={{
            slotProps: {
              backdrop: { invisible: true },
              paper: {
                sx: glassDropdown
                  ? {
                      background: glass.background,
                      backdropFilter: glass.backdropBlur,
                      border: `0.5px solid ${glass.border}`,
                      boxShadow: glass.shadow,
                      borderRadius: 3,
                      mt: 1,
                    }
                  : {},
              },
            },
          }}
          {...props}
        >
          {optionsWithLockState.map(renderOption)}
        </Select>
        {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
      </StyledFormControl>
    );
  },
);

GlassSelect.displayName = 'GlassSelect';

export default GlassSelect;
